import * as React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LearningModule } from '../../../amorg-common';
import { getLearningModules, getLearningModulesPublic } from '../../../api/learningClientApi';
import { Button } from '../../../designSystem/button/Button';
import { LinkButton } from '../../../designSystem/button/LinkButton';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { iconName } from '../../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../designSystem/PageHeader';
import { P } from '../../../designSystem/typography/Body';
import { H2, H3 } from '../../../designSystem/typography/Heading';


type LearningModuleContentProps = {
    isPublic?: boolean;
    onModuleTitleChanged?: (title:string) => void;
}


export function LearningModuleContent (props: LearningModuleContentProps): JSX.Element
{

    const [loading, setLoading] = React.useState(true);
    const [module, setModule] = React.useState<LearningModule | null>(null);

    const { moduleId } = useParams<{ moduleId: string; }>();

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const moduleList = props.isPublic ? await getLearningModulesPublic() : await getLearningModules();
            const selectedModule = moduleList.find(m => m.id === moduleId);
            setModule(selectedModule || null);
            props.onModuleTitleChanged?.(selectedModule?.title || ''

            );
            setLoading(false);
        };
        getData();
    }, []);


    const headerIcon: iconName = module?.icon as iconName;

    return (
        <>
        <PageHeader 
            headerIcon={headerIcon}
            headerText={module?.title || ''}
            narrow
        />
        <SingleColumnLayout narrow>
            <Flex flexDirection='column' gap={16}>

                <LoadingPageOverlay loading={loading} />
                {!loading && module &&
                    <Flex flexDirection='column' gap={32}>
                        


                        
                        <ContentAttention>
                            <Flex flexDirection='row' gap={32} justifyContent='space-between' alignItems='flex-start'>
                                <P>Indholdet på denne side er udarbejdet af CreceaJoblife A/S, som sikrer faglighed, kvalitet og relevant ekspertise. Alt materiale er produceret med fokus på at give dig den bedst mulige information.</P>
                                <img width="160px" src="/images/partner/creceajoblife/CreceaJoblife_white.svg" />
                            </Flex>
                        </ContentAttention>
                        {module.description &&
                            <P dangerouslySetInnerHTML={{ __html: module.description }} />
                        }
                        

                        <section>
                            {module.pages.map((page, index) => (
                                <Flex flexDirection='column' key={index}>
                                    {page.pageContent.map((content, contentIndex) => (
                                        <div key={contentIndex}>
                                            
                                            {content.type === 'html' && (
                                                <>
                                                {content.header &&    
                                                    <H3 dangerouslySetInnerHTML={{ __html: content.header }} />
                                                }
                                                {content.content &&
                                                    <div dangerouslySetInnerHTML={{ __html: content.content }} />
                                                }
                                                </>
                                            )}

                                            {content.type === 'html-attention' && (
                                                <ContentAttention>
                                                {content.header &&     
                                                    <H3><div dangerouslySetInnerHTML={{ __html: content.header }} /></H3>
                                                }
                                                {content.content &&
                                                    <div dangerouslySetInnerHTML={{ __html: content.content }} />
                                                }
                                                </ContentAttention>
                                            )}

                                            {content.type === 'header' && (
                                                <>
                                                    {(!content.variant || content.variant === 'h2') && <H2>{content.content}</H2>}
                                                    {content.variant === 'h3' && <H3>{content.content}</H3>}
                                                </>
                                            )}

                                            {content.type === 'image' && (
                                                                                                    
                                                <img src={`/images/${content.imageUrl}`} alt={content.altText} title={content.titleText} style={{ maxWidth: '100%', height: 'auto' }} />
                                                
                                            )}

                                            {content.type === 'video' && (
                                                
                                                    <iframe 
                                                        width="1000" 
                                                        height="562" 
                                                        src={`https://www.youtube.com/embed/${content.videoId}`}
                                                        title="YouTube video player" 
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                                        referrerPolicy="strict-origin-when-cross-origin" 
                                                        frameBorder="0"
                                                        allowFullScreen></iframe>
                                            
                                            )}
                                            
                                                
                                                
                                            {(() => {
                                                const isButtonType =
                                                    content.type === 'pdf-download' || content.type === 'cta-button';

                                                if (isButtonType && content.groupById) {
                                                    const groupItems = page.pageContent.filter(
                                                        c =>
                                                            (c.type === 'pdf-download' || c.type === 'cta-button') &&
                                                            c.groupById === content.groupById
                                                    );

                                                    const firstIndex = page.pageContent.findIndex(
                                                        c =>
                                                            (c.type === 'pdf-download' || c.type === 'cta-button') &&
                                                            c.groupById === content.groupById
                                                    );

                                                    if (contentIndex !== firstIndex) return null;

                                                    return (
                                                        <Flex justifyContent="space-between" gap={16}>
                                                            {groupItems.map((item, i) => (
                                                                <React.Fragment key={i}>
                                                                    {item.type === 'pdf-download' && (
                                                                        <LinkButton
                                                                            href={`/pdf/learning/${item.pdfUrl}`}
                                                                            target="_blank"
                                                                            variant="filled"
                                                                            icon="interface_actions_download"
                                                                            text={item.pdfName}
                                                                            download={item.pdfName}
                                                                        />
                                                                    )}
                                                                    {item.type === 'cta-button' && (
                                                                        <Button
                                                                            variant="filled"
                                                                            size={"52px"}
                                                                            text={item.text}
                                                                            onClick={() => window.open(item.url, '_blank')}
                                                                        />
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </Flex>
                                                    );
                                                }

                                                if (content.type === 'pdf-download') {
                                                    return (
                                                        <LinkButton
                                                            href={`/pdf/learning/${content.pdfUrl}`}
                                                            target="_blank"
                                                            variant="filled"
                                                            icon="interface_actions_download"
                                                            text={content.pdfName}
                                                            download={content.pdfName}
                                                        />
                                                    );
                                                }

                                                if (content.type === 'cta-button') {
                                                    return (
                                                        <Button
                                                            variant="filled"
                                                            size={"52px"}
                                                            text={content.text}
                                                            onClick={() => window.open(content.url, '_blank')}
                                                        />
                                                    );
                                                }

                                                return null;
                                            })()}

                                            
                                            {content.type === 'section-empty' && (
                                                <Flex marginBotttom={content.height ? content.height: 32}>
                                                </Flex>
                                            )}
                                            
                                        </div>
                                    ))}
                                </Flex>
                            ))}
                        </section>
                    </Flex>
                }
            
            </Flex>
        </SingleColumnLayout>
        </>

    );
}


const ContentAttention = styled.div`
    background-color: #3c0f11;
    box-shadow: inset 0 9px 9px -5px rgba(0,0,0,0.5);
    padding: 1rem;
    border-radius: 4px;
    color:white;

    p {
        color: #ccc;
    }
`

